import React, { useState, useEffect, ChangeEvent } from "react";
import { TextField, Button, Box, Paper, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { LocalizationProvider, DateField } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MuiTelInput } from "mui-tel-input";
import dayjs, { Dayjs } from "dayjs";

// Define the type for each field configuration
interface FieldConfig {
  label: string;
  type: "text" | "number" | "phone" | "date" | "dropdown" | "textarea";
  nonEditable?: boolean;
  required?: boolean;
  options?: string[];
  defaultValue?: string;
}

// Define the props for the EditableInfo component with generic type T
interface EditableInfoProps<T extends Record<string, any>> {
  label: string;
  data: T;
  fields: Record<string, FieldConfig>;
  onSave: (data: T) => void;
  initialEditing: boolean;
}

const EditableInfo = <T extends Record<string, any>>({
  label,
  data,
  fields,
  onSave,
  initialEditing,
}: EditableInfoProps<T>) => {
  const [isEditing, setIsEditing] = useState<boolean>(initialEditing);
  const [formData, setFormData] = useState<T>(data);

  // Update internal state when data prop changes (but only if different from current formData)
  useEffect(() => {
    // Only update if the data is actually different to prevent infinite loops
    const dataKeys = Object.keys(data);
    const isDataDifferent = dataKeys.some(key => data[key] !== formData[key]) ||
                            Object.keys(formData).length !== dataKeys.length;
    if (isDataDifferent) {
      setFormData(data);
    }
  }, [data, formData]);

  // Handle field change to update state
  const handleChange = (field: keyof T, value: any) => {
    const newData = {
      ...formData,
      [field]: value,
    };
    setFormData(newData);
    onSave(newData);
  };

  // Save changes and exit editing mode
  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  // Toggle edit mode
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Render the correct input field based on the type
  const renderField = (field: keyof T, config: FieldConfig) => {
    const { label, type, required, options, defaultValue } = config;
    // Use !== undefined to allow empty string and 0 as valid values
    const value = formData[field] !== undefined ? formData[field] : (defaultValue || "");

    if (!isEditing || !!config.nonEditable) {
      return (
        <Box key={String(field)} sx={{ mb: 2 }}>
          <strong>{label}:</strong> {String(value)}
        </Box>
      );
    }

    switch (type) {
      case "dropdown":
        const isOther = options && !options.includes(String(value)) && value !== "";
        const displayValue = isOther || (value !== "" && !options?.includes(String(value))) ? "other" : value || "";
        return (
          <Box key={String(field)} sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{label}</InputLabel>
              <Select
                value={displayValue}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (selectedValue === "other") {
                    handleChange(field, "custom");
                  } else {
                    handleChange(field, selectedValue);
                  }
                }}
                label={label}
                required={required}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === "other") {
                    return `Custom Quantity${value && value !== "custom" ? `: ${value}` : ""}`;
                  }
                  return selected || "";
                }}
              >
                {options?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <Box 
              sx={{ 
                mt: 1,
                height: (displayValue === "other" || (value && !options?.includes(String(value)))) ? 'auto' : 0,
                overflow: 'hidden',
                transition: 'height 0.3s ease-in-out, opacity 0.3s ease-in-out',
                opacity: (displayValue === "other" || (value && !options?.includes(String(value)))) ? 1 : 0,
              }}
            >
              <TextField
                label={`${label} (Other)`}
                variant="outlined"
                fullWidth
                type="number"
                value={value === "custom" ? "" : value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const inputValue = e.target.value;
                  // Only allow positive integers
                  if (inputValue === "" || (/^\d+$/.test(inputValue) && parseInt(inputValue) > 0)) {
                    handleChange(field, inputValue);
                  }
                }}
                required={required}
                placeholder="Enter custom value"
                inputProps={{ min: 1, step: 1 }}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'white'
                  }
                }}
              />
            </Box>
          </Box>
        );
      case "number":
        return (
          <TextField
            key={String(field)}
            label={label}
            variant="outlined"
            fullWidth
            type="number"
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const inputValue = e.target.value;
              // Only allow non-negative integers (0 and positive)
              if (inputValue === "" || (/^\d+$/.test(inputValue) && parseInt(inputValue) >= 0)) {
                handleChange(field, inputValue);
              }
            }}
            sx={{ mb: 2 }}
            required={required}
            inputProps={{ min: 0, step: 1 }}
          />
        );
      case "phone":
        return (
          <MuiTelInput
            key={String(field)}
            label={label}
            fullWidth
            value={value}
            onChange={(value) => handleChange(field, value)}
            defaultCountry={"US"}
            sx={{ mb: 2 }}
          />
        );
      case "date":
        // Ensure that the value passed to DateField is a Dayjs object
        const dateValue = dayjs(value); // Convert to Dayjs object
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs} key={String(field)}>
            <DateField
              label={label}
              fullWidth
              value={dateValue}
              onChange={(newValue: Dayjs | null) =>
                handleChange(field, newValue)
              }
              sx={{ mb: 2 }}
            />
          </LocalizationProvider>
        );
      case "textarea":
        return (
          <TextField
            key={String(field)}
            label={label}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange(field, e.target.value)
            }
            sx={{ mb: 2 }}
            required={required}
          />
        );
      default:
        return (
          <TextField
            key={String(field)}
            label={label}
            variant="outlined"
            fullWidth
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange(field, e.target.value)
            }
            sx={{ mb: 2 }}
            required={required}
          />
        );
    }
  };

  return (
    <Paper
      sx={{ padding: { xs: "12px 0", sm: 2 }, border: { xs: "none", sm: "1px solid #ccc" }, borderBottom: { xs: "1px solid #eee", sm: "1px solid #ccc" } }}
      elevation={0} // Removes the drop shadow
    >
      <Typography variant="h5" align="left" marginBottom="20px">
        {label}
      </Typography>
      {Object.entries(fields).map(([field, config]) =>
        renderField(field as keyof T, config),
      )}
      {!isEditing && (
        <Button variant="outlined" color="primary" onClick={handleEdit}>
          Edit
        </Button>
      )}
    </Paper>
  );
};

export default EditableInfo;
